/**
 * Controls the HTML HUD overlay: action buttons, exit button, worker tabs.
 * Only visible in INTERIOR state.
 */

import { WORKER_NAMES } from '@/utils/constants';
import { dataManager } from '@/data/data-manager';
import { DeliverablesPanel } from '@/ui/deliverables-panel';

export class HUD {
  private hudEl: HTMLElement;
  private exitBtn: HTMLElement;
  private workerTabsEl: HTMLElement;
  private selectedWorkerIndex = 0;
  private workerCount = 1;
  private deliverablesPanel: DeliverablesPanel;
  private workBadgeEl: HTMLElement | null = null;

  constructor() {
    this.hudEl = document.getElementById('hud')!;
    this.exitBtn = document.getElementById('exit-btn')!;
    this.workerTabsEl = document.getElementById('worker-tabs')!;
    this.deliverablesPanel = new DeliverablesPanel();

    this.setupListeners();
    this.setupWorkButton();
  }

  private setupListeners(): void {
    // Action buttons
    const actionBtns = this.hudEl.querySelectorAll('.action-btn');
    actionBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        const action = (btn as HTMLElement).dataset.action;
        if (!action) return;

        if (action === 'add-coworker') {
          window.dispatchEvent(new CustomEvent('add-coworker'));
          return;
        }

        if (action === 'work') {
          this.deliverablesPanel.toggle();
          return;
        }

        // Dispatch action for the selected worker
        window.dispatchEvent(new CustomEvent('action-requested', {
          detail: { actionId: action, workerIndex: this.selectedWorkerIndex },
        }));

        // Update active state on buttons
        actionBtns.forEach((b) => b.classList.remove('active'));
        btn.classList.add('active');
      });
    });

    // Exit button
    this.exitBtn.addEventListener('click', () => {
      window.dispatchEvent(new CustomEvent('exit-interior'));
    });
  }

  show(): void {
    this.hudEl.classList.add('visible');
    this.exitBtn.classList.add('visible');
    this.updateWorkerTabs();
  }

  hide(): void {
    this.hudEl.classList.remove('visible');
    this.exitBtn.classList.remove('visible');
    this.workerTabsEl.classList.remove('visible');
  }

  setWorkerCount(count: number): void {
    this.workerCount = count;
    this.updateWorkerTabs();
  }

  getSelectedWorkerIndex(): number {
    return this.selectedWorkerIndex;
  }

  private setupWorkButton(): void {
    // Listen for badge updates from the deliverables panel
    window.addEventListener('deliverable-badge-update', ((e: CustomEvent) => {
      const { count } = e.detail as { count: number };
      this.updateWorkBadge(count);
    }) as EventListener);

    // Also refresh badge when a new deliverable arrives via WebSocket
    window.addEventListener('task-completed', () => {
      // Small delay to let the deliverable be stored
      setTimeout(() => {
        if (!this.deliverablesPanel) return;
        this.deliverablesPanel.refresh();
      }, 1000);
    });

    // Refresh when the plan-level merged deliverable is created
    window.addEventListener('plan-completed', () => {
      // Longer delay — the merge AI call just finished, give D1 time to commit
      setTimeout(() => {
        if (!this.deliverablesPanel) return;
        this.deliverablesPanel.refresh();
      }, 2000);
    });
  }

  private updateWorkBadge(count: number): void {
    const workBtn = this.hudEl.querySelector('.hud-work-btn');
    if (!workBtn) return;

    // Remove existing badge
    this.workBadgeEl?.remove();
    this.workBadgeEl = null;

    if (count > 0) {
      this.workBadgeEl = document.createElement('span');
      this.workBadgeEl.className = 'hud-work-badge';
      this.workBadgeEl.textContent = String(count);
      workBtn.appendChild(this.workBadgeEl);
    }
  }

  private updateWorkerTabs(): void {
    if (this.workerCount <= 1) {
      this.workerTabsEl.classList.remove('visible');
      this.workerTabsEl.innerHTML = '';
      return;
    }

    this.workerTabsEl.classList.add('visible');
    this.workerTabsEl.innerHTML = '';

    for (let i = 0; i < this.workerCount; i++) {
      const tab = document.createElement('button');
      tab.className = `worker-tab${i === this.selectedWorkerIndex ? ' active' : ''}`;
      tab.textContent = (dataManager.initialized ? dataManager.getWorkerName(i) : null) || WORKER_NAMES[i] || `Worker ${i + 1}`;
      tab.addEventListener('click', () => {
        this.selectedWorkerIndex = i;
        this.updateWorkerTabs();
        // Notify chat panel and other listeners about worker selection change
        window.dispatchEvent(new CustomEvent('worker-selected', {
          detail: { workerIndex: i },
        }));
      });
      this.workerTabsEl.appendChild(tab);
    }
  }
}
